import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button,
  FormControl, InputLabel, Select, MenuItem, Box, Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { taskActions, tagActions } from '../../store';
import { RichTextEditor } from '../richTextEditor';
import { LaborSection } from '../laborSection';
import { PendingLaborSection } from '../pendingLaborSection';
import { TagComponent } from '../tags';
import { FileUpload } from '../fileUpload';
import { CommentSection } from '../commentSection';

export const CreateTaskDialog = ({ open, onClose, editTask }) => {
  const dispatch = useDispatch();
  const tags = useSelector((state) => state.tags.tags);
  const projects = useSelector((state) => state.projects.projects);
  const allUsers = useSelector((state) => state.users.users);
  const myProfile = useSelector((state) => state.auth.myProfile);

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [pendingLabor, setPendingLabor] = useState([]);
  const [formData, setFormData] = useState({
    title: '', description: '', type: 'task', priority: 'medium', status: 'todo',
    startDate: '', endDate: '', tags: [], project: '', attachments: [],
  });

  useEffect(() => {
    if (editTask) {
      setFormData({
        title: editTask.title || '', description: editTask.description || '',
        type: editTask.type || 'task', priority: editTask.priority || 'medium',
        status: editTask.status || 'todo',
        startDate: editTask.startDate ? editTask.startDate.split('T')[0] : '',
        endDate: editTask.endDate ? editTask.endDate.split('T')[0] : '',
        tags: editTask.tags?.map((t) => t._id || t) || [],
        project: editTask.project?._id || editTask.project || '',
        attachments: editTask.attachments || [],
      });
    } else {
      setFormData({
        title: '', description: '', type: 'task', priority: 'medium', status: 'todo',
        startDate: '', endDate: '', tags: [], project: '', attachments: [],
      });
    }
    if (!editTask) setPendingLabor([]);
    setConfirmDelete(false);
  }, [editTask, open]);

  // Refetch tags when project changes so the TagComponent shows the right project's tags
  useEffect(() => {
    if (formData.project) {
      dispatch(tagActions.getTags({ project: formData.project }));
    }
  }, [dispatch, formData.project]);

  const handleChange = (field) => (e) => setFormData({ ...formData, [field]: e.target.value });

  const selectedProject = (projects || []).find((p) => p._id === formData.project);
  const membersList = selectedProject?.members?.length ? selectedProject.members : (allUsers || []);

  const handleSubmit = () => {
    const payload = { ...formData };
    if (!payload.project) delete payload.project;
    if (!payload.startDate) delete payload.startDate;
    if (!payload.endDate) delete payload.endDate;
    if (payload.tags.length === 0) delete payload.tags;
    if (payload.attachments.length === 0) delete payload.attachments;
    if (editTask) {
      dispatch(taskActions.editTask(editTask._id, payload));
    } else {
      if (pendingLabor.length > 0) payload.pendingLabor = pendingLabor;
      dispatch(taskActions.createTask(payload));
    }
    onClose();
  };

  const scheduleThisWeek = () => {
    const today = new Date();
    const day = today.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    const monday = new Date(today); monday.setDate(today.getDate() + diff);
    const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6);
    const fmt = (d) => d.toISOString().split('T')[0];
    setFormData({ ...formData, startDate: fmt(monday), endDate: fmt(sunday) });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ fontWeight: 600, pb: 1 }}>
        {editTask ? `Edit Task ${editTask.taskNumber || ''}` : 'Create Task'}
      </DialogTitle>
      <DialogContent sx={{ pt: '8px !important' }}>
        <Box sx={{ display: 'flex', gap: 3 }}>
          {/* Left column — main content */}
          <Box sx={{ flex: 2, minWidth: 0 }}>
            <TextField fullWidth label="Title" value={formData.title} size="small"
              onChange={handleChange('title')} margin="dense" required autoFocus />
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>Description</Typography>
              <RichTextEditor content={formData.description}
                onChange={(html) => setFormData({ ...formData, description: html })}
                placeholder="Describe the task..." />
            </Box>
          </Box>

          {/* Right column — metadata */}
          <Box sx={{ flex: 1, minWidth: 220 }}>
            <FormControl fullWidth size="small" margin="dense">
              <InputLabel>Project</InputLabel>
              <Select value={formData.project} onChange={handleChange('project')} label="Project">
                <MenuItem value=""><em>No Project</em></MenuItem>
                {(projects || []).map((p) => (
                  <MenuItem key={p._id} value={p._id}>{p.name} ({p.key})</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <FormControl fullWidth size="small" margin="dense">
                <InputLabel>Type</InputLabel>
                <Select value={formData.type} onChange={handleChange('type')} label="Type">
                  <MenuItem value="bug">Bug</MenuItem>
                  <MenuItem value="story">Story</MenuItem>
                  <MenuItem value="task">Task</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth size="small" margin="dense">
                <InputLabel>Priority</InputLabel>
                <Select value={formData.priority} onChange={handleChange('priority')} label="Priority">
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="critical">Critical</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <FormControl fullWidth size="small" margin="dense">
              <InputLabel>Status</InputLabel>
              <Select value={formData.status} onChange={handleChange('status')} label="Status">
                <MenuItem value="todo">To Do</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="in_review">In Review</MenuItem>
                <MenuItem value="done">Done</MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField fullWidth label="Start" type="date" size="small" margin="dense"
                value={formData.startDate}
                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                InputLabelProps={{ shrink: true }} />
              <TextField fullWidth label="End" type="date" size="small" margin="dense"
                value={formData.endDate}
                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                InputLabelProps={{ shrink: true }} />
            </Box>
            <Button size="small" variant="text" onClick={scheduleThisWeek}
              sx={{ textTransform: 'none', fontSize: 12, px: 0, mt: 0.25 }}>
              This week
            </Button>
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5, display: 'block' }}>Tags</Typography>
              <TagComponent
                taskId={editTask?._id}
                taskTags={(tags || []).filter((t) => formData.tags.includes(t._id))}
                projectId={formData.project}
                onTagsChange={(ids) => setFormData({ ...formData, tags: ids })}
              />
            </Box>
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5, display: 'block' }}>Attachments</Typography>
              <FileUpload
                attachments={formData.attachments}
                onChange={(atts) => setFormData({ ...formData, attachments: atts })}
              />
            </Box>
          </Box>
        </Box>
        {/* Work Items — full width */}
        {editTask ? (
          <LaborSection taskId={editTask._id} projectMembers={membersList} />
        ) : (
          <PendingLaborSection entries={pendingLabor} onChange={setPendingLabor}
            members={membersList} currentUserId={myProfile?._id} />
        )}
        {/* Comments — only for existing tasks */}
        {editTask && <CommentSection taskId={editTask._id} />}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
        <Box>
          {editTask && (
            confirmDelete ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 500 }}>
                  Delete this task?
                </Typography>
                <Button size="small" onClick={() => { dispatch(taskActions.deleteTask(editTask._id)); onClose(); }}
                  sx={{ textTransform: 'none', color: 'error.main', fontWeight: 600 }}>
                  Yes, delete
                </Button>
                <Button size="small" onClick={() => setConfirmDelete(false)}
                  sx={{ textTransform: 'none', color: 'text.secondary' }}>
                  No
                </Button>
              </Box>
            ) : (
              <Button size="small" startIcon={<DeleteOutlineIcon />}
                onClick={() => setConfirmDelete(true)}
                sx={{ textTransform: 'none', color: 'text.disabled', '&:hover': { color: 'error.main' } }}>
                Delete
              </Button>
            )
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={!formData.title}
            sx={{ textTransform: 'none', fontWeight: 600 }}>
            {editTask ? 'Save Changes' : 'Create Task'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
